import { useState, useEffect, useCallback, useRef } from 'react';
import { calculateAllHealthScores } from '../services/healthScoreService';

/**
 * useClientHealth
 *
 * Manages health score calculation for all "Client"-tagged contacts.
 * Scores are held in component state (not persisted) and recalculated:
 *   - On mount
 *   - When contacts, tasks, or invoices change (debounced 800 ms)
 *   - On demand via recalculate()
 */
export function useClientHealth({ contacts = [], tasks = [], invoices = [] }) {
  const [scores, setScores] = useState({});
  const [lastCalc, setLastCalc] = useState(null);

  // Ref so the stable recalculate callback always reads fresh data
  const dataRef = useRef({ contacts, tasks, invoices });
  useEffect(() => {
    dataRef.current = { contacts, tasks, invoices };
  }, [contacts, tasks, invoices]);

  const recalculate = useCallback(() => {
    const { contacts: c, tasks: t, invoices: inv } = dataRef.current;
    const fresh = calculateAllHealthScores(c, t, inv);
    setScores(fresh);
    setLastCalc(new Date().toISOString());
    return fresh;
  }, []);

  // Full recalc on mount
  useEffect(() => {
    recalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced recalc when source data changes
  useEffect(() => {
    const timer = setTimeout(recalculate, 800);
    return () => clearTimeout(timer);
  }, [contacts, tasks, invoices, recalculate]);

  const summary = Object.values(scores).reduce(
    (acc, s) => {
      if (s.score >= 80) acc.healthy++;
      else if (s.score >= 50) acc.attention++;
      else acc.atRisk++;
      return acc;
    },
    { healthy: 0, attention: 0, atRisk: 0 }
  );

  return { scores, recalculate, lastCalc, summary };
}
